import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { XRPC } from '@atcute/client';
import {
  OAuthUserAgent,
  configureOAuth,
  createAuthorizationUrl,
  deleteStoredSession,
  finalizeAuthorization,
  getSession,
  listStoredSessions,
  resolveFromIdentity,
} from '@atcute/oauth-browser-client';
import type { ActorProfile } from '../domain/types';
import { recordSkyspaceActivity } from '../data/site-services';
import {
  cleanOAuthCallbackUrl,
  finishOAuthCallbackNavigation,
  modernApplicationPath,
  modernOAuthRedirectUri,
  oauthCallbackParams,
  OAUTH_RETURN_PATH_KEY,
  withOAuthCallbackTimeout,
} from './oauth-callback';
import { blueskyAppViewHeaders } from '../data/appview';
import { dashboardExperimentEnabled } from '../features/home/experiment';
import {
  automaticSessionDid,
  PAUSED_OAUTH_SESSION_KEY,
  rememberedSessionDid,
} from './session-policy';
import {
  isGranularSkyspaceSession,
  skyspaceOAuthScope,
} from '../../../src/skyspace-oauth-scope.mjs';

let callbackPromise: Promise<OAuthUserAgent> | undefined;

if (typeof window !== 'undefined') {
  configureOAuth({
    metadata: {
      client_id: `${window.location.origin}/client-metadata.json`,
      redirect_uri: modernOAuthRedirectUri(
        window.location.origin,
        window.location.pathname,
      ),
    },
    storageName: 'atcute-oauth',
  });
}

export interface AuthState {
  agent?: OAuthUserAgent;
  rpc?: XRPC;
  did?: string;
  handle?: string;
  displayName?: string;
  avatar?: string;
  pending: boolean;
  error?: string;
  login(handle: string): Promise<void>;
  logout(): Promise<void>;
}

export const AuthContext = createContext<AuthState | undefined>(undefined);

async function profileFor(rpc: XRPC, actor: string): Promise<ActorProfile> {
  const response = await rpc.request({
    type: 'get',
    nsid: 'app.bsky.actor.getProfile',
    headers: blueskyAppViewHeaders(),
    params: { actor },
  });
  return response.data as ActorProfile;
}

function agentForSession(session: Awaited<ReturnType<typeof getSession>>): OAuthUserAgent {
  return new OAuthUserAgent(session);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [agent, setAgent] = useState<OAuthUserAgent>();
  const [profile, setProfile] = useState<ActorProfile>();
  const [pending, setPending] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let disposed = false;
    const restore = async () => {
      const callbackParams = oauthCallbackParams(
        window.location.hash,
        window.location.search,
      );
      try {
        let restoredAgent: OAuthUserAgent | undefined;
        if (callbackParams) {
          callbackPromise ||= finalizeAuthorization(callbackParams).then(session => {
            if (!isGranularSkyspaceSession(session, window.location.hostname)) {
              deleteStoredSession(session.info.sub);
              throw new Error('SkySpace needs you to approve its updated account permissions.');
            }
            return agentForSession(session);
          });
          restoredAgent = await withOAuthCallbackTimeout(callbackPromise);
          if (!disposed) {
            history.replaceState(null, '', cleanOAuthCallbackUrl(window.location.pathname));
            localStorage.removeItem(OAUTH_RETURN_PATH_KEY);
            localStorage.removeItem(PAUSED_OAUTH_SESSION_KEY);
          }
        } else {
          const did = automaticSessionDid(
            listStoredSessions(),
            localStorage.getItem(PAUSED_OAUTH_SESSION_KEY),
          );
          if (did) {
            const session = await getSession(did, { allowStale: true });
            if (isGranularSkyspaceSession(session, window.location.hostname)) {
              restoredAgent = agentForSession(session);
            } else {
              deleteStoredSession(did);
              localStorage.removeItem(PAUSED_OAUTH_SESSION_KEY);
            }
          }
        }
        if (!restoredAgent || disposed) return;
        const rpc = new XRPC({ handler: restoredAgent });
        const actor = await withOAuthCallbackTimeout(profileFor(rpc, restoredAgent.sub));
        if (disposed) return;
        setAgent(restoredAgent);
        setProfile(actor);
        localStorage.setItem('bskyHandle', actor.handle);
        recordSkyspaceActivity(restoredAgent.sub, actor.handle, 'login');
        void import('../private-state/runtime').then(({ startPrivateStateSync }) => (
          startPrivateStateSync(rpc, restoredAgent.sub)
        ));
        if (window.location.hash || window.location.search === '') {
          const destination = dashboardExperimentEnabled()
            ? 'home=feed'
            : encodeURIComponent(actor.handle);
          history.replaceState(null, '', `${window.location.pathname}?${destination}`);
          window.dispatchEvent(new PopStateEvent('popstate'));
        }
      } catch (reason) {
        if (!disposed) {
          if (callbackParams) {
            finishOAuthCallbackNavigation(window.location.pathname);
            localStorage.removeItem(OAUTH_RETURN_PATH_KEY);
          }
          setError(reason instanceof Error ? reason.message : 'Could not restore your Bluesky session.');
        }
      } finally {
        if (!disposed) setPending(false);
      }
    };
    void restore();
    return () => {
      disposed = true;
    };
  }, []);

  const login = useCallback(async (rawHandle: string) => {
    setError(undefined);
    setPending(true);
    try {
      const handle = rawHandle.trim().replace(/^@/, '');
      if (!handle) throw new Error('Enter your Bluesky handle first.');
      const normalized = handle.includes('.') ? handle : `${handle}.bsky.social`;
      localStorage.setItem(
        OAUTH_RETURN_PATH_KEY,
        modernApplicationPath(window.location.pathname),
      );
      const { identity, metadata } = await resolveFromIdentity(normalized);
      const storedDid = rememberedSessionDid(listStoredSessions(), identity.id);
      if (storedDid) {
        try {
          const session = await getSession(storedDid);
          if (!isGranularSkyspaceSession(session, window.location.hostname)) {
            throw new Error('SkySpace account permissions have changed.');
          }
          const restoredAgent = agentForSession(session);
          const rpc = new XRPC({ handler: restoredAgent });
          const actor = await withOAuthCallbackTimeout(profileFor(rpc, restoredAgent.sub));
          setAgent(restoredAgent);
          setProfile(actor);
          localStorage.setItem('bskyHandle', actor.handle);
          localStorage.removeItem(OAUTH_RETURN_PATH_KEY);
          localStorage.removeItem(PAUSED_OAUTH_SESSION_KEY);
          recordSkyspaceActivity(restoredAgent.sub, actor.handle, 'login');
          void import('../private-state/runtime').then(({ startPrivateStateSync }) => (
            startPrivateStateSync(rpc, restoredAgent.sub)
          ));
          const destination = dashboardExperimentEnabled()
            ? 'home=feed'
            : encodeURIComponent(actor.handle);
          history.replaceState(null, '', `${window.location.pathname}?${destination}`);
          window.dispatchEvent(new PopStateEvent('popstate'));
          setPending(false);
          return;
        } catch {
          deleteStoredSession(storedDid);
        }
      }
      const url = await createAuthorizationUrl({
        metadata,
        identity,
        scope: skyspaceOAuthScope(window.location.hostname),
      });
      await new Promise(resolve => window.setTimeout(resolve, 200));
      window.location.assign(url);
    } catch (reason) {
      localStorage.removeItem(OAUTH_RETURN_PATH_KEY);
      setError(reason instanceof Error ? reason.message : 'Could not start Bluesky sign-in.');
      setPending(false);
      throw reason;
    }
  }, []);

  const logout = useCallback(async () => {
    setPending(true);
    try {
      if (agent) localStorage.setItem(PAUSED_OAUTH_SESSION_KEY, agent.sub);
    } finally {
      localStorage.removeItem('bskyHandle');
      void import('../private-state/runtime').then(({ stopPrivateStateSync }) => {
        stopPrivateStateSync({ preserveSession: true });
      });
      setAgent(undefined);
      setProfile(undefined);
      setPending(false);
    }
  }, [agent]);

  const value = useMemo<AuthState>(() => ({
    agent,
    rpc: agent ? new XRPC({ handler: agent }) : undefined,
    did: agent?.sub,
    handle: profile?.handle,
    displayName: profile?.displayName,
    avatar: profile?.avatar,
    pending,
    error,
    login,
    logout,
  }), [
    agent,
    error,
    login,
    logout,
    pending,
    profile?.avatar,
    profile?.displayName,
    profile?.handle,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider.');
  return value;
}
